import { useEffect, useRef } from 'react';
import { cn } from '@/shared/lib/cn';
import { revealLine } from '@/shared/lib/motion';
import type { TestPodLine } from '../model/test-pod';

type TestPodLogProps = { lines: readonly TestPodLine[]; emptyText: string };

/** The dark log block; it stays dark in both themes (Foundations: Log line). */
export function TestPodLog({ lines, emptyText }: TestPodLogProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const shownRef = useRef(lines.length);

  useEffect(() => {
    const items = listRef.current?.querySelectorAll('li');
    const last = items?.[items.length - 1];
    const isNewLine = lines.length > shownRef.current;
    shownRef.current = lines.length;
    if (!last || !isNewLine) return undefined;
    return revealLine(last);
  }, [lines.length]);

  return (
    <ol
      ref={listRef}
      aria-live="polite"
      className="min-h-44 rounded-tile bg-term px-4 py-3.5 font-mono text-mono text-term-ink leading-5.5"
    >
      {lines.length === 0 ? <li className="text-term-muted">{emptyText}</li> : null}
      {lines.map((line) => (
        <li
          key={line.key}
          className={cn(
            'grid grid-cols-[48px_minmax(0,1fr)] gap-3 shell:grid-cols-[56px_minmax(0,1fr)_auto]',
            line.isError && 'text-term-error',
          )}
        >
          <span className="text-term-key">{line.label}</span>
          <span>{line.text}</span>
          <span className="hidden text-term-muted shell:inline">{line.detail}</span>
        </li>
      ))}
    </ol>
  );
}
